/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Paginator Component
 * ----------
 * reusable pagination component with ellipsis and callbacks.
 * Props:
 *  - page: current page number (1-based)
 *  - pageCount: total number of pages
 *  - onPrevious: function(page) called when Previous clicked with target page (page-1)
 *  - onPage: function(page) called when specific page clicked
 *  - onNext: function(page) called when Next clicked (page+1)
 *  - maxPagesToShow: maximum pages to show on the center , by default 3.
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";

export default function Paginator({
  page,
  pageCount,
  onPrevious,
  onPage,
  onNext,
  maxPagesToShow = 3,
}) {
  const { theme } = useTheme();

  if (pageCount <= 1) return null;

  const getPageItems = () => {
    const pages = [];
    const half = Math.floor(maxPagesToShow / 2);

    const startPage = Math.max(2, page - half);
    const endPage = Math.min(pageCount - 1, page + half);

    const adjustedStart = startPage - 2 < 0 ? 2 : startPage;
    const adjustedEnd = pageCount - 1 - endPage < 0 ? pageCount - 1 : endPage;

    pages.push({ type: "page", number: 1 });

    if (adjustedStart > 2) pages.push({ type: "ellipsis", key: "start" });

    for (let i = adjustedStart; i <= adjustedEnd; i++) {
      pages.push({ type: "page", number: i });
    }

    if (adjustedEnd < pageCount - 1) pages.push({ type: "ellipsis", key: "end" });

    if (pageCount > 1) pages.push({ type: "page", number: pageCount });

    return pages;
  };

  const pageItems = getPageItems();

  // Smaller button style for mini version
  const baseButtonStyle = {
    fontWeight: 600,
    border: `1px solid ${theme.primary}`,
    borderRadius: theme.buttonRadius,
    minWidth: 26,
    minHeight: 26,
    width: 26,
    height: 26,
    fontSize: 12,
    margin: "0 1px",
    outline: "none",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        padding: "8px 0",
        flexWrap: "wrap", // allows responsiveness
        paddingTop:20
      }}
    >
      {/* Previous button */}
      <button
        aria-label="Previous"
        disabled={page === 1}
        onClick={() => page > 1 && onPrevious(page - 1)}
        style={{
          ...baseButtonStyle,
          fontWeight: 700,
          fontSize: 14,
          background: "#fff",
          color: page === 1 ? "#aaa" : theme.primary,
          cursor: page === 1 ? "not-allowed" : "pointer",
          borderColor: page === 1 ? "#aaa" : theme.primary,
          userSelect: "none",
        }}
      >
        &lt;
      </button>

      {/* Page buttons and ellipsis */}
      {pageItems.map((item) => {
        if (item.type === "page") {
          const isCurrent = item.number === page;
          return (
            <button
              key={item.number}
              aria-label={`Go to page ${item.number}`}
              disabled={isCurrent}
              onClick={() => !isCurrent && onPage(item.number)}
              style={{
                ...baseButtonStyle,
                background: isCurrent ? theme.primary : "#fff",
                color: isCurrent ? "#fff" : theme.primary,
                fontWeight: isCurrent ? 700 : 600,
                cursor: isCurrent ? "not-allowed" : "pointer",
                userSelect: "none",
              }}
            >
              {item.number}
            </button>
          );
        } else if (item.type === "ellipsis") {
          return (
            <button
              key={item.key}
              disabled
              aria-hidden="true"
              style={{
                ...baseButtonStyle,
                borderColor: "transparent",
                cursor: "default",
                userSelect: "none",
                padding: "0 4px",
                minWidth: "auto",
                width: "auto",
              }}
            >
              ...
            </button>
          );
        }
        return null;
      })}

      {/* Next button */}
      <button
        aria-label="Next"
        disabled={page === pageCount}
        onClick={() => page < pageCount && onNext(page + 1)}
        style={{
          ...baseButtonStyle,
          fontWeight: 700,
          fontSize: 14,
          background: "#fff",
          color: page === pageCount ? "#aaa" : theme.primary,
          cursor: page === pageCount ? "not-allowed" : "pointer",
          borderColor: page === pageCount ? "#aaa" : theme.primary,
          userSelect: "none",
        }}
      >
        &gt;
      </button>
    </div>
  );
}
